import React, { useState, useEffect } from 'react';
import { FileText, ChevronDown, ChevronUp, Clock, Users, CheckCircle, Search, Filter, RefreshCw } from 'lucide-react';

// --- Configuration ---
// Update this after deploying the Worker
const API_BASE = process.env.REACT_APP_TRANSCRIPT_API || 'https://rsc-transcript-api.terrydwisely.workers.dev';

const PROJECTS = [
  { key: 'all', label: 'All Projects' },
  { key: 'Osage Casino Ponca City Expansion', label: 'Osage Casino', job: '11-25938' },
  { key: 'Autumn Park Apartments', label: 'Autumn Park', job: '22-25951' },
  { key: 'Chickasaw Nation CDC', label: 'Chickasaw CDC', job: '22-23846' },
  { key: 'Country Oaks Apartments', label: 'Country Oaks', job: '' },
  { key: 'Unassigned', label: 'Unassigned' },
];

const PROJECT_COLORS = {
  'Osage Casino Ponca City Expansion': 'bg-amber-100 text-amber-800 border-amber-300',
  'Autumn Park Apartments': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Chickasaw Nation CDC': 'bg-sky-100 text-sky-800 border-sky-300',
  'Country Oaks Apartments': 'bg-purple-100 text-purple-800 border-purple-300',
  'Unassigned': 'bg-slate-100 text-slate-600 border-slate-300',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function truncate(str, len = 200) {
  if (!str) return '';
  return str.length > len ? str.substring(0, len) + '...' : str;
}

// --- Transcript Card ---
const TranscriptCard = ({ transcript, onExpand, expanded }) => {
  const colors = PROJECT_COLORS[transcript.project_name] || PROJECT_COLORS['Unassigned'];
  const attendees = Array.isArray(transcript.attendees) ? transcript.attendees : [];
  const actionItems = Array.isArray(transcript.action_items) ? transcript.action_items : [];

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      {/* Card Header */}
      <button
        onClick={() => onExpand(transcript.id)}
        className="w-full text-left p-4 focus:outline-none"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-xs font-bold px-2 py-0.5 rounded border ${colors}`}>
                {transcript.project_name}
              </span>
              {transcript.job_number && (
                <span className="text-xs text-slate-400">Job #{transcript.job_number}</span>
              )}
            </div>
            <h3 className="font-bold text-slate-900 text-sm leading-tight mb-1">
              {transcript.meeting_title}
            </h3>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(transcript.meeting_date)}
              </span>
              {transcript.duration && (
                <span>{transcript.duration}</span>
              )}
              {attendees.length > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {attendees.length} attendee{attendees.length !== 1 ? 's' : ''}
                </span>
              )}
              {actionItems.length > 0 && (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {actionItems.length} action item{actionItems.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {!expanded && transcript.summary && (
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                {truncate(transcript.summary)}
              </p>
            )}
          </div>
          <div className="ml-3 text-slate-400 flex-shrink-0">
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4">
          {/* Summary */}
          {transcript.summary && (
            <div className="mt-3">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Summary</h4>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{transcript.summary}</p>
            </div>
          )}

          {/* Attendees */}
          {attendees.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Attendees</h4>
              <div className="flex flex-wrap gap-1">
                {attendees.map((a, i) => (
                  <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                    {typeof a === 'string' ? a : a.name || a.email || JSON.stringify(a)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Items */}
          {actionItems.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Action Items</h4>
              <ul className="space-y-1">
                {actionItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>{typeof item === 'string' ? item : item.text || item.description || JSON.stringify(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Full Transcript */}
          {transcript.full_transcript && (
            <div className="mt-3">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Full Transcript</h4>
              <div className="bg-slate-50 rounded border border-slate-200 p-3 max-h-96 overflow-y-auto">
                <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap font-mono">
                  {transcript.full_transcript}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Main Panel ---
const TranscriptPanel = () => {
  const [transcripts, setTranscripts] = useState([]);
  const [projectCounts, setProjectCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTranscripts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (selectedProject !== 'all') {
        params.set('project', selectedProject);
      }
      const res = await fetch(`${API_BASE}/api/transcripts?${params}`);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();
      setTranscripts(data.transcripts || []);
      setProjectCounts(data.project_counts || []);
    } catch (err) {
      setError(err.message);
      setTranscripts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranscripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const filteredTranscripts = searchTerm
    ? transcripts.filter(
        (t) =>
          t.meeting_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.summary?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : transcripts;

  const handleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      // If we don't have the full transcript loaded, fetch it
      const existing = transcripts.find((t) => t.id === id);
      if (existing && !existing.full_transcript) {
        fetch(`${API_BASE}/api/transcripts/${id}`)
          .then((res) => res.json())
          .then((data) => {
            if (data.success) {
              setTranscripts((prev) =>
                prev.map((t) => (t.id === id ? { ...t, ...data.transcript } : t))
              );
            }
          })
          .catch(() => {});
      }
      setExpandedId(id);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-900" />
          <h2 className="text-lg font-bold text-slate-900">Meeting Transcripts</h2>
          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">
            {filteredTranscripts.length}
          </span>
        </div>
        <button
          onClick={fetchTranscripts}
          className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {/* Project filter */}
        <div className="flex items-center gap-2 flex-1">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {PROJECTS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
                {projectCounts.find((c) => c.project_name === p.key)
                  ? ` (${projectCounts.find((c) => c.project_name === p.key).count})`
                  : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search transcripts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="text-sm border border-slate-300 rounded pl-8 pr-3 py-1.5 w-full sm:w-64 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Project count pills */}
      {projectCounts.length > 0 && selectedProject === 'all' && (
        <div className="flex flex-wrap gap-2 mb-4">
          {projectCounts.map((pc) => {
            const colors = PROJECT_COLORS[pc.project_name] || PROJECT_COLORS['Unassigned'];
            return (
              <button
                key={pc.project_name}
                onClick={() => setSelectedProject(pc.project_name)}
                className={`text-xs font-medium px-2.5 py-1 rounded border ${colors} hover:opacity-80 transition-opacity`}
              >
                {pc.project_name} ({pc.count})
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      {loading && (
        <div className="text-center py-12">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-slate-500">Loading transcripts...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-sm text-red-600 font-medium">Failed to load transcripts</p>
          <p className="text-xs text-red-500 mt-1">{error}</p>
          <button
            onClick={fetchTranscripts}
            className="mt-2 text-xs bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && filteredTranscripts.length === 0 && (
        <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500 font-medium">No transcripts found</p>
          <p className="text-xs text-slate-400 mt-1">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Transcripts will appear here after meetings are processed through Otter.ai'}
          </p>
        </div>
      )}

      {!loading && !error && filteredTranscripts.length > 0 && (
        <div className="space-y-3">
          {filteredTranscripts.map((t) => (
            <TranscriptCard
              key={t.id}
              transcript={t}
              expanded={expandedId === t.id}
              onExpand={handleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TranscriptPanel;
